import { NavigateFunction } from 'react-router-dom';

// Extract keys from the interface type
export const EMBED_PARAM_KEYS = ['embed', 'source', 'userAlias'] as const;

// Embed plumbing params that must NOT leak into a path reported to the FinSuite parent.
// These are FinSuite's own iframe query params (added to the iframe src); reporting them
// back would pollute the parent's external link and URL hash. Includes `isDarkMode`, which
// EMBED_PARAM_KEYS omits because navigation does not need to preserve it.
const REPORTED_PATH_STRIP_KEYS = ['embed', 'source', 'userAlias', 'isDarkMode'] as const;

/**
 * Builds the clean, app-relative path to report to the FinSuite parent via
 * FINSUITE_CURRENT_PATH: `pathname` + `search` (minus embed plumbing params) + `hash`.
 *
 * The result always starts with '/', so it satisfies the parent's relative-path sanitizer.
 *
 * NOTE — the app's own hash is preserved raw (e.g. `/reports/123#row-4`). When FinSuite mirrors
 * this into its own URL hash the result has two `#` (`/app/xpt#/reports/123#row-4`). This is
 * intentional: the raw `#` keeps the external "open in new tab" link a real fragment. Encoding
 * it to `%23` would put a literal `%23` in that link instead. The parent's hash-restore parser
 * owns splitting on only the first `#` (documented on the FinSuite side).
 *
 * @param pathname - Current location pathname (always starts with '/')
 * @param search - Current location search string (may include a leading '?')
 * @param hash - Current location hash string (may include a leading '#')
 */
export const buildEmbedReportedPath = (pathname: string, search: string, hash: string): string => {
  const params = new URLSearchParams(search);
  REPORTED_PATH_STRIP_KEYS.forEach((key) => params.delete(key));
  const cleanSearch = params.toString();
  return `${pathname}${cleanSearch ? `?${cleanSearch}` : ''}${hash || ''}`;
};

/**
 * Type guard for a safe, same-document relative path received from the parent (e.g. a
 * FINSUITE_NAVIGATE_TO restore target derived from a user-pasted URL hash). Only a value
 * beginning with a single '/' is allowed — rejects protocol-relative ('//evil.com'),
 * absolute ('https://…'), scheme, and backslash forms before they can reach router navigation.
 */
export const isSafeReportedPath = (path: unknown): path is string =>
  typeof path === 'string' && path.startsWith('/') && !path.startsWith('//') && !path.startsWith('/\\');

/**
 * Utility function to navigate while preserving embed parameters
 * @param navigate - React Router navigate function
 * @param path - The path to navigate to
 * @param preserveEmbedParams - Whether to preserve embed-related query parameters (default: true)
 */
export const navigateWithEmbedParams = (
  navigate: NavigateFunction,
  path: string,
  preserveEmbedParams: boolean = true,
  options?: { replace?: boolean }
): void => {
  if (!preserveEmbedParams) {
    navigate(path);
    return;
  }

  const finalPath = appendEmbedParamsToPath(path);

  if (options) {
    navigate(finalPath, options);
  } else {
    navigate(finalPath);
  }
};

/**
 * Utility function to extract embed parameters from current URL
 * @returns URLSearchParams containing only embed parameters
 */
const getEmbedParams = (): URLSearchParams => {
  const currentParams = new URLSearchParams(window.location.search);
  const embedParams = new URLSearchParams();

  EMBED_PARAM_KEYS.forEach((key) => {
    const value = currentParams.get(key);
    if (value !== null) {
      embedParams.set(key, value);
    }
  });

  return embedParams;
};

/**
 * Utility function to get current embed parameters as a query string
 * @returns Query string with embed parameters (without leading ?)
 */
export const getEmbedParamsQueryString = (): string => {
  return getEmbedParams().toString();
};

/**
 * Utility function to append embed parameters to a given path
 * @param path - The base path
 * @returns Path with embed parameters appended
 */
export const appendEmbedParamsToPath = (path: string): string => {
  const [basePath, fragment] = path.split('#');
  const [pathWithoutParams, existingParamsString] = basePath.split('?');
  const existingParams = new URLSearchParams(existingParamsString || '');

  // Get current embed params and merge with existing params
  const embedParams = getEmbedParams();
  EMBED_PARAM_KEYS.forEach((key) => {
    const value = embedParams.get(key);
    if (value !== null) {
      existingParams.set(key, value);
    }
  });

  // Construct the final path
  const paramsString = existingParams.toString();
  return paramsString
    ? `${pathWithoutParams}?${paramsString}${fragment ? '#' + fragment : ''}`
    : `${pathWithoutParams}${fragment ? '#' + fragment : ''}`;
};
